<template>
    <div class="episodes-list-container box">
        <div class="box-title">
            <div class="episodes-list-actions">
                <span>Episodes</span>

                <base-submit-button
                    class="btn add-btn is-small"
                    :disabled="episodeModal.isAudioFileUploading()"
                    @click="openNewEpisodeModal"
                >
                    New Episode
                </base-submit-button>
            </div>

            <div class="episodes-list-menu">
                <episodes-list-nav
                    :episodes-count="episodesCount"
                    :value="status"
                    @change="onStatusChange"
                />

                <base-search-input
                    placeholder="Search episode..."
                    @input="onSearchInput"
                />
            </div>
        </div>
        <div class="box-content">
            <transition name="fade" mode="out-in">
                <base-loader v-if="isLoading" />
                <div v-else>
                    <episodes-list-items
                        :episodes="episodes"
                        :show="show"
                        :order="order"
                        :page="page"
                        @order-change="onOrderChange"
                        @page-change="onPageChange"
                    />
                </div>
            </transition>
        </div>
    </div>
</template>
<script>
import episodeModal from '@/utils/episodeModal';
import { debounce } from 'lodash';
import { createNamespacedHelpers } from 'vuex';
import EpisodesListItems from './EpisodesListItems';
import EpisodesListNav from './EpisodesListNav';

const { mapState, mapGetters } = createNamespacedHelpers('episode');

export default {
    components: {
        EpisodesListItems,
        EpisodesListNav,
    },

    props: {
        show: {
            type: [Object, Boolean],
            default: () => ({}),
        },
    },

    data: () => ({
        isLoading: true,
        order: {
            by: null,
            direction: null,
        },
        page: 1,
        search: '',
        status: null,
        episodeModal,
    }),

    computed: {
        ...mapState([
            'episodesCount',
        ]),

        ...mapGetters({
            episodes: 'filteredEpisodes',
        }),
    },

    /**
     * Mounts component.
     */
    async mounted() {
        await this.loadEpisodesAndCount();
    },

    methods: {
        /**
         * Fetches partal data.
         */
        async loadEpisodes() {
            this.isLoading = true;
            await this.loadEpisodesProxy();
            this.isLoading = false;
        },

        /**
         * Fetches all data.
         */
        async loadEpisodesAndCount() {
            this.isLoading = true;
            await Promise.all([
                this.loadEpisodesProxy(),
                this.loadEpisodesCountProxy(),
            ]);
            this.isLoading = false;
        },

        /**
         * Fetches episodes.
         */
        async loadEpisodesProxy() {
            await this.$store.dispatch('episode/loadEpisodes', {
                hash: this.$route.params.hash,
                query: {
                    order_by: this.order.by,
                    order_dir: this.order.direction,
                    page: this.page,
                    search: this.search || null,
                    status: this.status,
                },
            });
        },

        /**
         * Fetches episodes count.
         */
        async loadEpisodesCountProxy() {
            await this.$store.dispatch('episode/loadEpisodesCount', {
                hash: this.$route.params.hash,
                query: {
                    search: this.search || null,
                },
            });
        },

        /**
         * Handles status change event.
         *
         * @param  {string|null} status Selected status.
         */
        async onStatusChange(status) {
            this.page = 1;
            this.status = status;
            await this.loadEpisodes();
        },

        /**
         * Handles search input event.
         *
         * @param  {string} search Searched value.
         */
        onSearchInput: debounce(async function (search) {
            this.page = 1;
            this.search = search;
            await this.loadEpisodesAndCount();
        }, 500),

        /**
         * Handles order change event.
         *
         * @param  {object} order           Order data.
         * @param  {string} order.by        Selected column.
         * @param  {string} order.direction Selected
         */
        async onOrderChange({ by, direction }) {
            this.order.by = by;
            this.order.direction = direction;
            await this.loadEpisodes();
        },

        /**
         * Handles page change event.
         *
         * @param  {number} page Selected page.
         */
        async onPageChange(page) {
            this.page = page;
            await this.loadEpisodes();
        },

        /**
         * Opens new episode modal.
         */
        openNewEpisodeModal() {
            episodeModal.setInitialData(this.show, { context: 'create' });
            episodeModal.open();
        },
    },
};
</script>
